import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AccountDisplayStatus, AccountState, AccountType, QrCodeSearchCriteria} from 'web-angular-client';
import {AccountStatusUtil} from '../../../sdk/interface/accountStatusUtil';
import {SearchService} from '../../../sdk/component/search/search.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {ActivatedRoute} from '@angular/router';
import {PaymentSearchComponent} from '../../payment/payment-search/payment-search.component';
import {QrCodeSearchCriteriaImpl} from '../../../sdk/component/search/qr-code-search-criteria';
import {FilterPersistenceService} from '../../../sdk/component/search/filter-persistence.service';
import {DropdownItem} from '../../../sdk/interface/data/DropdownItem';
import {toUtcIsoAtLocalEndOfDay, toUtcIsoAtLocalStartOfDay} from '../../../sdk/util/searchUtil';

@Component({
    selector: 'app-qr-search',
    templateUrl: './qr-search.component.html',
    styleUrls: ['./qr-search.component.scss'],
    standalone: false
})
export class QrSearchComponent extends PaymentSearchComponent implements OnInit, OnDestroy
{
    form: UntypedFormGroup;
    AccountType = AccountType;
    @Input() accountType: AccountType;
    private qrCodeSearchCriteria: QrCodeSearchCriteria;
    private _DESCRIPTION_INPUT_ID = 'description';
    private DESCRIPTION_FILTER_ID = 'descriptionFilter';

    constructor(
        protected searchDataService: SearchService,
        protected filterPersistenceService: FilterPersistenceService,
        protected route: ActivatedRoute,
        protected accountStatusUtil: AccountStatusUtil,
        protected formBuilder: UntypedFormBuilder,
        protected ngxLoader: NgxUiLoaderService,
        protected activatedRoute: ActivatedRoute)
    {
        super(
            searchDataService,
            filterPersistenceService,
            route,
            accountStatusUtil,
            formBuilder,
            ngxLoader,
            activatedRoute);
    }

    get description()
    {
        return this.qrCodeSearchCriteria.description;
    }

    set description(val: string)
    {
        this.qrCodeSearchCriteria.description = <string> val;
    }

    get statusOptions(): DropdownItem[]
    {
        if (this.accountType === AccountType.QR_CODE)
        {
            return [
                {label: 'ANY', value: null},
                {label: 'ACTIVE', value: AccountDisplayStatus.PAYMENT_OUTSTANDING},
                {label: 'CANCELLED', value: AccountDisplayStatus.PAYMENT_CANCELLED}
            ];
        }
        else
        {
            return super.statusOptions;
        }
    }

    ngOnInit(): void
    {
        this.form = this.formBuilder.group({
            dateTimeCreatedLower: [{value: this.dateCreatedLower}],
            dateTimeCreatedUpper: [{value: this.dateCreatedUpper}],
            amountLower: [''],
            amountUpper: [''],
            description: [''],
            displayStatus: [null]
        });

        this.qrCodeSearchCriteria = new QrCodeSearchCriteriaImpl(this.accountType);

        this.initializeSearchPage();
    }

    ngOnDestroy(): void
    {
        clearInterval(this.interval);
    }

    setSearchCriteria(event: any)
    {
        const inputId = event.target.id;
        const val = event.target.value;
        this.setAccountSearchCriteria(inputId, val);
    }

    setAccountSearchCriteria(inputId: string, val: any)
    {
        switch (inputId)
        {
            case this._DATE_TIME_CREATED_LOWER_INPUT_ID:
                this.qrCodeSearchCriteria.dateTimeCreatedLower = toUtcIsoAtLocalStartOfDay(val);
                break;
            case this._DATE_TIME_CREATED_UPPER_INPUT_ID:
                this.qrCodeSearchCriteria.dateTimeCreatedUpper = toUtcIsoAtLocalEndOfDay(val);
                break;
            case this._DESCRIPTION_INPUT_ID:
                this.qrCodeSearchCriteria.description = val.toString();
                break;
        }
    }

    clear(activeTab: string)
    {
        switch (activeTab)
        {
            case this.DATE_CREATED_FILTER_ID:
                this.qrCodeSearchCriteria.dateTimeCreatedLower = null;
                this.qrCodeSearchCriteria.dateTimeCreatedUpper = null;
                this.clearDateInputField(this.form, 'dateTimeCreatedLower');
                this.clearDateInputField(this.form, 'dateTimeCreatedUpper');
                break;
            case this.DESCRIPTION_FILTER_ID:
                this.description = null;
                this.form.get('description').reset();
                break;
            case this.ACCOUNT_STATUS_FILTER_ID:
                this.qrCodeSearchCriteria.state = null;
                this.form.get('displayStatus').reset(null);
                break;
        }
        this.search();
    }

    refresh()
    {
        this.searchDataService.searchableCriteria = this.qrCodeSearchCriteria;
        this.saveCurrentFilters();
        this.searchDataService.refreshSearch(this.qrCodeSearchCriteria);
    }

    getSearchCriteria(): any
    {
        return this.qrCodeSearchCriteria;
    }

    disableClear(activeTab: string): boolean
    {
        switch (activeTab)
        {
            case this.DATE_CREATED_FILTER_ID:
                return !(this.form.get(this._DATE_TIME_CREATED_LOWER_INPUT_ID).dirty || this.form.get(this._DATE_TIME_CREATED_UPPER_INPUT_ID).dirty);
            case this.DESCRIPTION_FILTER_ID:
                return !(this.form.get(this._DESCRIPTION_INPUT_ID).dirty);
            case this.ACCOUNT_STATUS_FILTER_ID:
                return !this.form.get(this._DISPLAY_STATUS_INPUT_ID).dirty;
            default:
                return true;
        }
    }

    clearAll()
    {
        this.qrCodeSearchCriteria = new QrCodeSearchCriteriaImpl(this.accountType);
        this.form.reset(this.qrCodeSearchCriteria);
        this.search();
    }

    updateQrDisplayStatus(savedQrCodeSearchCriteria: any): void
    {
        const status = savedQrCodeSearchCriteria?.state?.[0];

        const displayStatus = status === AccountState.PAYMENT_OUTSTANDING
            ? AccountDisplayStatus.PAYMENT_OUTSTANDING
            : status === AccountState.PAYMENT_CANCELLED
                ? AccountDisplayStatus.PAYMENT_CANCELLED
                : null;

        if (displayStatus !== null)
        {
            this.form.patchValue({displayStatus});
            this.form.get('displayStatus')?.markAsDirty();
        }
    }

    setSearchCriteriaStatus(status: any)
    {
        this.qrCodeSearchCriteria.state = this.accountStatusUtil.getAccountStateFromDescription(
            status);
        this.form.get('displayStatus').setValue(status);
    }

    setFilters(): void
    {
        this.applyFilters(
            this.form,
            (criteria: QrCodeSearchCriteria) => this.qrCodeSearchCriteria = criteria,
            (criteria: QrCodeSearchCriteria) => this.updateQrDisplayStatus(criteria)
        );
    }
}